/**
 * Modèle unifié de constats d'audit ("audit findings").
 *
 * Point d'entrée unique qui transforme TOUTES les données d'un audit complet
 * (scores projet, erreurs techniques, recommandations IA, positionnement GEO,
 * audit visuel) en une structure simple, lisible et priorisée :
 *
 *   {
 *     scores   : { global, seo, performance, technique, visuel, geo },
 *     verdict  : { tier, label, tone, text, weakest },
 *     findings : Finding[]      // tous les constats, triés par priorité
 *     quickWins: Finding[]      // effort faible + impact réel
 *     chantiers: Finding[]      // gros travaux à planifier
 *     forces   : string[]       // ce qui fonctionne déjà
 *     counts   : { high, medium, low, total }
 *     ai       : { summary }    // résumé IA éventuel
 *     geo      : extractGeoInsights(...)
 *   }
 *
 * Chaque Finding est normalisé :
 *   { id, pilier, severity, effort, title, why, action, pages[], count, source }
 *
 * Cette source unique alimente la page d'accueil "État des lieux", les onglets
 * détaillés et l’export JSON rapport client pour garantir un
 * message cohérent partout.
 */

import {
  flattenTechnicalErrors,
  errorGroupKey,
  SEV_ORDER,
} from './technicalErrors';
import {
  translateError,
  normalizeCategoryKey,
  effortInfo,
  EFFORT,
} from './auditDictionary';
import { verdictGlobal, globalTier } from './reportNarrative';
import { extractGeoInsights } from './syntheseInsights';
import { collectPageFindings } from './commonFindings';

// ── Piliers (vocabulaire client unique) ─────────────────────────────────────

export const PILIERS = {
  seo: { key: 'seo', label: 'SEO & contenu' },
  performance: { key: 'performance', label: 'Performance' },
  technique: { key: 'technique', label: 'Technique' },
  visibilite: { key: 'visibilite', label: 'Visibilité IA' },
  visuel: { key: 'visuel', label: 'Visuel' },
};

/** Convertit un axe du dictionnaire en pilier de synthèse. */
function axeToPilier(axe) {
  switch (axe) {
    case 'performance':
    case 'mobile':
      return 'performance';
    case 'technique':
    case 'accessibilite':
      return 'technique';
    case 'seo':
    case 'social':
    default:
      return 'seo';
  }
}

/** Mappe une catégorie de recommandation IA vers un pilier. */
function aiCategoryToPilier(category) {
  const c = normalizeCategoryKey(category);
  if (c.includes('perf')) return 'performance';
  if (c.includes('technique') || c.includes('accessib')) return 'technique';
  if (c.includes('geo') || c.includes('ia') || c.includes('llm')) return 'visibilite';
  return 'seo';
}

// ── Sévérité / impact ────────────────────────────────────────────────────────

const SEVERITY_WEIGHT = { high: 3, medium: 2, low: 1 };

/** Normalise un impact texte (FR ou EN) en sévérité. */
function impactToSeverity(impact) {
  const v = String(impact || '').toLowerCase();
  if (v.includes('élev') || v.includes('elev') || v.includes('high') || v.includes('fort') || v.includes('critique') || v.includes('haut')) {
    return 'high';
  }
  if (v.includes('faible') || v.includes('low') || v.includes('mineur') || v.includes('bas')) {
    return 'low';
  }
  return 'medium';
}

// ── Déduplication textuelle ──────────────────────────────────────────────────

function normText(s) {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function titlesSimilar(a, b) {
  const na = normText(a);
  const nb = normText(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.length > 18 && nb.length > 18 && (na.includes(nb) || nb.includes(na))) return true;
  return false;
}

// ── Sources de constats ──────────────────────────────────────────────────────

/**
 * Regroupe toutes les erreurs techniques par catégorie (toutes pages confondues).
 * @returns {Finding[]}
 */
function findingsFromTechnicalErrors(technicalErrorsData, project) {
  const rows = flattenTechnicalErrors(technicalErrorsData, project);
  if (!rows.length) return [];

  const groups = new Map();
  for (const row of rows) {
    const key = errorGroupKey(row);
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        category: row.category,
        severity: row.severity,
        recommendation: row.recommendation,
        type: row.type,
        rows: [],
        urls: new Set(),
      });
    }
    const g = groups.get(key);
    g.rows.push(row);
    g.urls.add(row.url);
    if ((SEV_ORDER[row.severity] ?? 3) < (SEV_ORDER[g.severity] ?? 3)) g.severity = row.severity;
    if (!g.recommendation && row.recommendation) g.recommendation = row.recommendation;
  }

  return [...groups.values()].map((g) => {
    const t = translateError(g.rows[0]);
    const pages = [...g.urls].sort((a, b) => a.localeCompare(b, 'fr'));
    return {
      id: `tech:${g.key}`,
      pilier: axeToPilier(t?.axe || g.type),
      severity: g.severity || 'low',
      effort: t?.effort || 'variable',
      title: t?.label || g.category || 'Problème détecté',
      why: t?.pourquoi || '',
      action: g.recommendation || t?.corriger || '',
      pages,
      count: pages.length,
      source: 'audit',
    };
  });
}

/**
 * Recommandations prioritaires de l'IA (langage humain, déjà priorisées).
 * @returns {Finding[]}
 */
function findingsFromAi(aiRecommendations) {
  if (!aiRecommendations) return [];
  const out = [];
  const priority = aiRecommendations.priority_recommendations || [];
  priority.forEach((reco, i) => {
    const title = reco.title || reco.tache || reco.task || '';
    if (!title) return;
    const pages = Array.isArray(reco.affected_pages) ? reco.affected_pages : [];
    out.push({
      id: `ia:${i}:${normText(title).slice(0, 40)}`,
      pilier: aiCategoryToPilier(reco.category),
      severity: impactToSeverity(reco.impact),
      effort: reco.effort || 'variable',
      title,
      why: reco.description || '',
      action: reco.example || reco.action || '',
      pages,
      count: pages.length,
      source: 'ia',
    });
  });
  return out;
}

// ── Tri / priorisation ───────────────────────────────────────────────────────

function priorityScore(f) {
  const sev = SEVERITY_WEIGHT[f.severity] ?? 1;
  // Un problème présent sur plusieurs pages pèse plus lourd.
  const reach = 1 + Math.min(1.5, Math.log10((f.count || 1) + 1));
  // Léger bonus aux recommandations IA (formulées pour l'humain).
  const sourceBonus = f.source === 'ia' ? 0.25 : 0;
  return sev * reach + sourceBonus;
}

function sortFindings(list) {
  return [...list].sort((a, b) => priorityScore(b) - priorityScore(a));
}

/** Fusionne les sources en dédupliquant par titre similaire (axe identique). */
function mergeFindings(sources) {
  const merged = [];
  for (const f of sources) {
    const dup = merged.find((m) => m.pilier === f.pilier && titlesSimilar(m.title, f.title));
    if (dup) {
      // Conserve la sévérité la plus forte et complète l'action / l'impact manquants.
      if ((SEVERITY_WEIGHT[f.severity] ?? 0) > (SEVERITY_WEIGHT[dup.severity] ?? 0)) {
        dup.severity = f.severity;
      }
      if (!dup.why && f.why) dup.why = f.why;
      if (!dup.action && f.action) dup.action = f.action;
      if (f.pages?.length) {
        const set = new Set([...(dup.pages || []), ...f.pages]);
        dup.pages = [...set];
        dup.count = dup.pages.length;
      }
      // Une action IA prime sur un libellé d'audit brut.
      if (f.source === 'ia' && dup.source !== 'ia') dup.title = f.title;
      continue;
    }
    merged.push({ ...f });
  }
  return merged;
}

// ── Forces ───────────────────────────────────────────────────────────────────

function buildForces(scores, geo) {
  const forces = [];
  if (scores.seo >= 60) forces.push('Vos pages sont bien optimisées pour le référencement Google.');
  if (scores.performance > 80) forces.push('Le site se charge rapidement, y compris sur mobile.');
  if (scores.technique >= 80) forces.push('Les fondations techniques (HTTPS, indexation) sont saines.');
  if (scores.visuel != null && scores.visuel > 80) forces.push('L\'expérience visuelle des pages clés est convaincante.');
  if (scores.geo != null && scores.geo >= 60) forces.push('Le site est déjà visible dans les réponses des IA (ChatGPT, Gemini…).');
  for (const p of (geo?.pointsForts || []).slice(0, 3)) {
    const t = typeof p === 'string' ? p : (p?.titre || p?.title || p?.description || '');
    if (t) forces.push(t);
  }
  return forces.slice(0, 5);
}

// ── Scores ─────────────────────────────────────────────────────────────────

function computeScores(project, visualAudit, positioning) {
  const visualPages = (visualAudit?.pages || []).filter((p) => !p?.error && p?.analysis);
  const visualVals = visualPages
    .map((p) => Number(p?.analysis?.score_visuel))
    .filter((n) => Number.isFinite(n) && n > 0);
  const visuel = visualVals.length
    ? Math.round(visualVals.reduce((a, b) => a + b, 0) / visualVals.length)
    : null;

  const geoRaw = positioning?.raw_json?.geo_score || {};
  const geo = geoRaw.score_global_geo ?? positioning?.score_positionnement ?? null;

  return {
    global: Math.round(Number(project?.score_global) || 0),
    seo: Math.round(Number(project?.score_seo) || 0),
    performance: Math.round(Number(project?.score_performance) || 0),
    technique: Math.round(Number(project?.score_technique) || 0),
    visuel,
    geo: geo != null ? Math.round(Number(geo)) : null,
  };
}

// ── Point d'entrée ───────────────────────────────────────────────────────────

/**
 * @param {object} input
 * @param {object}  input.project              Objet projet (scores persistés, méta)
 * @param {Array}   [input.audits]             Liste d'audits (getAudits)
 * @param {object}  [input.technicalErrorsData] Réponse getTechnicalErrors
 * @param {object}  [input.aiRecommendations]  Réponse getAIRecommendations
 * @param {object}  [input.positioning]        Analyse GEO (1re entrée)
 * @param {object}  [input.visualAudit]        project.visual_audit
 */
export function buildAuditFindings({
  project,
  audits = [],
  technicalErrorsData = null,
  aiRecommendations = null,
  positioning = null,
  visualAudit = null,
} = {}) {
  const scores = computeScores(project, visualAudit, positioning);
  const geo = extractGeoInsights(positioning);

  const techFindings = findingsFromTechnicalErrors(technicalErrorsData, project);
  const aiFindings = findingsFromAi(aiRecommendations);

  // L'IA d'abord (langage humain) puis l'audit déterministe pour compléter.
  const merged = mergeFindings([...aiFindings, ...techFindings]);
  const findings = sortFindings(merged);

  const quickWins = findings.filter(
    (f) => effortInfo(f.effort).rank <= 1 && SEVERITY_WEIGHT[f.severity] >= 2,
  );
  const chantiers = findings.filter((f) => effortInfo(f.effort).rank >= 3);

  const counts = findings.reduce(
    (acc, f) => {
      acc[f.severity] = (acc[f.severity] || 0) + 1;
      acc.total += 1;
      return acc;
    },
    { high: 0, medium: 0, low: 0, total: 0 },
  );

  const verdict = {
    ...globalTier(scores.global),
    text: verdictGlobal(scores.global, {
      seo: scores.seo,
      performance: scores.performance,
      technique: scores.technique,
    }),
  };

  return {
    scores,
    verdict,
    findings,
    quickWins,
    chantiers,
    forces: buildForces(scores, geo),
    counts,
    ai: { summary: aiRecommendations?.summary || '' },
    geo,
  };
}

/**
 * Construit la liste priorisée des constats d'UNE page de référence.
 *
 * Fusionne, pour une seule page, les opportunités PageSpeed, les erreurs
 * techniques et les critères SEO on-page échoués en une liste unique, triée
 * par priorité, au format attendu par <FindingsList /> (même vocabulaire que
 * la page d'accueil et les livrables).
 *
 * @param {{ url, seo, perf, errors }} page
 * @returns {Finding[]}
 */
/** Nombre maxi d'opportunités PageSpeed retenues par page (anti-bruit). */
const MAX_PERF_FINDINGS_PER_PAGE = 4;

/**
 * Concept on-page d'un constat (images, titre, meta…) pour dédupliquer les
 * critères SEO bruts déjà couverts par une erreur technique mieux formulée.
 */
function onPageConcept(title) {
  const t = normText(title);
  if (/alt|alternatif|image/.test(t)) return 'images';
  if (/titre|title/.test(t)) return 'title';
  if (/meta description|meta/.test(t)) return 'meta';
  if (/canoniq|canonical/.test(t)) return 'canonical';
  if (/(^| )h1( |$)/.test(t)) return 'h1';
  return null;
}

export function buildPageFindings(page) {
  const raw = collectPageFindings(page);

  // Déduplication par clé (conserve la sévérité la plus forte rencontrée).
  const byKey = new Map();
  for (const f of raw) {
    const prev = byKey.get(f.key);
    if (prev) {
      if ((SEVERITY_WEIGHT[f.severity] ?? 0) > (SEVERITY_WEIGHT[prev.severity] ?? 0)) {
        prev.severity = f.severity;
        prev.impact = Math.max(prev.impact ?? 0, f.impact ?? 0);
      }
      continue;
    }
    byKey.set(f.key, { ...f });
  }

  const deduped = [...byKey.values()];

  // Les critères SEO et erreurs techniques sont curatés -> on les garde tous.
  // Les opportunités PageSpeed peuvent être pléthoriques (diagnostics Lighthouse) :
  // on ne retient que les plus impactantes et on plafonne leur sévérité à "moyen"
  // (le score Performance de l'en-tête porte déjà le niveau d'alerte global).
  const perf = deduped
    .filter((f) => f.source === 'performance')
    .sort((a, b) => (b.impact ?? 0) - (a.impact ?? 0))
    .slice(0, MAX_PERF_FINDINGS_PER_PAGE)
    .map((f) => ({ ...f, severity: f.severity === 'high' ? 'medium' : f.severity }));
  const others = deduped.filter((f) => f.source !== 'performance');

  const findings = [...others, ...perf].map((f) => ({
    id: f.key,
    pilier: axeToPilier(f.axe || f.source),
    severity: f.severity || 'low',
    effort: f.effort || 'variable',
    title: f.label,
    why: f.pourquoi || '',
    action: f.corriger || '',
    pages: [],
    count: 1,
    source: f.source,
  }));

  // Déduplication par concept on-page : un critère SEO brut (source "seo") est
  // retiré si le même sujet est déjà couvert par une erreur technique/IA.
  const coveredConcepts = new Set(
    findings
      .filter((f) => f.source !== 'seo')
      .map((f) => onPageConcept(f.title))
      .filter(Boolean),
  );
  const deduplicated = findings.filter(
    (f) => !(f.source === 'seo' && coveredConcepts.has(onPageConcept(f.title))),
  );

  return sortFindings(deduplicated);
}

/** Niveau de sévérité -> style (badge unique partagé). */
export const SEVERITY_STYLE = {
  high: { label: 'Critique', dot: 'bg-red-500', chip: 'bg-red-50 text-red-700 border-red-200' },
  medium: { label: 'Moyen', dot: 'bg-orange-500', chip: 'bg-orange-50 text-orange-700 border-orange-200' },
  low: { label: 'Mineur', dot: 'bg-amber-400', chip: 'bg-amber-50 text-amber-700 border-amber-200' },
};

export { effortInfo, EFFORT };
